import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  isHighValueStrategyScore,
  isTaskOverdue,
  STRATEGY_SCORE_LABELS,
  type NetworkingTaskRow,
} from '../../lib/networkingTasks';

const DAYS_PER_ROW = 7;
const MAX_PAST_DAYS = 90;
const MAX_AHEAD_DAYS = 90;
const ROW_PITCH_EMPTY = 80;
/** Space above cards: spine + tick + stem + card header chrome. */
const ROW_HANG_BASE = 98;
/** Approx height per task chip (title can wrap 2 lines). */
const TASK_CHIP_H = 48;
const ROW_BOTTOM_PAD = 24;
const ROW_PITCH_MIN_BUSY = 168;
const LINE_Y_IN_ROW = 22;
const LINE_H = 8; // matches h-2 spine
const LINE_CENTER_Y = LINE_Y_IN_ROW + LINE_H / 2;
const SIDE_PAD = 16;
const DRAG_THRESHOLD_PX = 5;
const VIEWPORT_MAX_H = 'min(62vh, 560px)';
const TIMELINE_RANGE_KEY = 'lccto.tasks.timelineRange';

export type TimelineRange = 'week' | 'month';

export function readStoredTimelineRange(): TimelineRange {
  try {
    const v = localStorage.getItem(TIMELINE_RANGE_KEY);
    if (v === 'week' || v === 'month') return v;
  } catch {
    /* ignore */
  }
  return 'week';
}

export function writeStoredTimelineRange(range: TimelineRange) {
  try {
    localStorage.setItem(TIMELINE_RANGE_KEY, range);
  } catch {
    /* ignore */
  }
}

function pitchForDayLoad(taskCount: number, forceHang: boolean): number {
  if (taskCount <= 0 && !forceHang) return ROW_PITCH_EMPTY;
  const n = Math.max(taskCount, forceHang ? 1 : 0);
  return Math.max(ROW_PITCH_MIN_BUSY, ROW_HANG_BASE + n * TASK_CHIP_H + ROW_BOTTOM_PAD);
}

function dateKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDays(key: string, delta: number): string {
  const d = new Date(`${key}T12:00:00`);
  d.setDate(d.getDate() + delta);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function daysBetween(a: string, b: string): number {
  const ms = new Date(`${b}T12:00:00`).getTime() - new Date(`${a}T12:00:00`).getTime();
  return Math.round(ms / 86400000);
}

function alignToRowStart(key: string, origin: string): string {
  const offset = daysBetween(origin, key);
  const aligned = Math.floor(offset / DAYS_PER_ROW) * DAYS_PER_ROW;
  return addDays(origin, aligned);
}

function formatWeekSpan(startKey: string): string {
  const endKey = addDays(startKey, 6);
  const a = new Date(`${startKey}T12:00:00`);
  const b = new Date(`${endKey}T12:00:00`);
  const left = a.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const right = b.toLocaleDateString(undefined, {
    month: a.getMonth() === b.getMonth() ? undefined : 'short',
    day: 'numeric',
  });
  return `${left} – ${right}`;
}

function formatDayShort(key: string, todayKey: string): { title: string; sub: string } {
  const d = new Date(`${key}T12:00:00`);
  const title = d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
  const month = d.toLocaleDateString(undefined, { month: 'short' });
  let sub = month;
  if (key === todayKey) sub = 'Today';
  else if (key === addDays(todayKey, 1)) sub = 'Tomorrow';
  else if (key === addDays(todayKey, -1)) sub = 'Yesterday';
  return { title, sub };
}

function dayOrdinal(n: number): string {
  const j = n % 10;
  const k = n % 100;
  if (k >= 11 && k <= 13) return `${n}th`;
  if (j === 1) return `${n}st`;
  if (j === 2) return `${n}nd`;
  if (j === 3) return `${n}rd`;
  return `${n}th`;
}

function formatRelativeDropParts(
  key: string,
  todayKey: string,
): { dateLine: string; agoLine: string } {
  const d = new Date(`${key}T12:00:00`);
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  const month = d.toLocaleDateString(undefined, { month: 'short' });
  const dateLine = `${weekday} ${month} ${dayOrdinal(d.getDate())}`;
  const delta = daysBetween(todayKey, key);
  let agoLine = '';
  if (delta === 0) agoLine = 'today';
  else if (delta === 1) agoLine = 'tomorrow';
  else if (delta === -1) agoLine = 'yesterday';
  else if (delta > 1) agoLine = `in ${delta} days`;
  else agoLine = `${Math.abs(delta)} days ago`;
  return { dateLine, agoLine };
}

function applyDateKeepTime(originalIso: string, dayKey: string): string {
  const orig = new Date(originalIso);
  const [y, m, d] = dayKey.split('-').map(Number);
  if (Number.isNaN(orig.getTime())) {
    return new Date(`${dayKey}T09:00:00`).toISOString();
  }
  const next = new Date(orig);
  next.setFullYear(y, m - 1, d);
  return next.toISOString();
}

function dayUnderPoint(clientX: number, clientY: number): string | null {
  const el = document.elementFromPoint(clientX, clientY);
  const dayEl = el?.closest?.('[data-day-drop]') as HTMLElement | null;
  return dayEl?.dataset?.dayDrop ?? null;
}

type WeekRow = { rowIndex: number; days: string[] };

function buildWeekRows(anchorStart: string, rowCount: number): WeekRow[] {
  const rows: WeekRow[] = [];
  let cursor = anchorStart;
  for (let r = 0; r < rowCount; r++) {
    const days: string[] = [];
    for (let i = 0; i < DAYS_PER_ROW; i++) days.push(addDays(cursor, i));
    cursor = addDays(cursor, DAYS_PER_ROW);
    rows.push({ rowIndex: r, days });
  }
  return rows;
}

function monthBounds(todayKey: string): { startKey: string; endKey: string } {
  const d = new Date(`${todayKey}T12:00:00`);
  const pad = (n: number) => String(n).padStart(2, '0');
  const startKey = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const endKey = `${last.getFullYear()}-${pad(last.getMonth() + 1)}-${pad(last.getDate())}`;
  return { startKey, endKey };
}

function computeMonthWindow(todayKey: string): {
  start: string;
  rowCount: number;
} {
  const { startKey, endKey } = monthBounds(todayKey);
  const start = alignToRowStart(startKey, todayKey);
  const endAligned = alignToRowStart(endKey, todayKey);
  const span = daysBetween(start, endAligned) + DAYS_PER_ROW;
  return { start, rowCount: Math.max(4, Math.ceil(span / DAYS_PER_ROW)) };
}

interface TasksTimelineBoardProps {
  tasks: NetworkingTaskRow[];
  draggingId: string | null;
  onDragStart: (taskId: string) => void;
  onDragEnd: () => void;
  onOpenTask: (task: NetworkingTaskRow) => void;
  onReschedule: (taskId: string, nextDueIso: string) => Promise<void>;
  updatingId: string | null;
  /** Optional label for share email subject (e.g. epic title). */
  shareLabel?: string | null;
  range: TimelineRange;
}

function formatShareDate(iso: string): string {
  const key = dateKey(iso);
  const d = new Date(`${key}T12:00:00`);
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  const month = d.toLocaleDateString(undefined, { month: 'short' });
  return `${weekday} ${month} ${dayOrdinal(d.getDate())}, ${d.getFullYear()}`;
}

/** Chronological dump for email — date + order + title. */
function buildTimelineShareEmail(
  tasks: NetworkingTaskRow[],
  opts?: { label?: string | null; todayKey?: string },
): { subject: string; body: string } {
  const todayKey = opts?.todayKey ?? dateKey(new Date().toISOString());
  const ordered = [...tasks].sort((a, b) => {
    const da = new Date(a.due_at).getTime();
    const db = new Date(b.due_at).getTime();
    if (da !== db) return da - db;
    const sa = a.strategy_score ?? -1;
    const sb = b.strategy_score ?? -1;
    if (sb !== sa) return sb - sa;
    return (a.title || '').localeCompare(b.title || '');
  });

  const label = opts?.label?.trim() || 'Task timeline';
  const subject = `${label} — ordered by date`;
  const lines: string[] = [
    `Here are the tasks in expected order (${label}).`,
    `As of ${formatShareDate(todayKey + 'T12:00:00')}.`,
    '',
  ];

  if (ordered.length === 0) {
    lines.push('(No open tasks on this timeline.)');
  } else {
    ordered.forEach((t, i) => {
      const due = formatShareDate(t.due_at);
      const score =
        t.strategy_score != null && t.strategy_score >= 1 && t.strategy_score <= 5
          ? ` · ${t.strategy_score}/5`
          : '';
      const title = (t.title || 'Untitled').trim();
      lines.push(`${i + 1}. ${due} — ${title}${score}`);
    });
  }

  lines.push('', '— Sent from LCCTO Engine timeline');
  return { subject, body: lines.join('\n') };
}

async function openTimelineInGmail(subject: string, body: string): Promise<void> {
  const gmailBase = 'https://mail.google.com/mail/?view=cm&fs=1&tf=1';
  const fullUrl = `${gmailBase}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  // Stay under common browser URL limits; if too long, copy body and open with a short note.
  if (fullUrl.length > 1800) {
    try {
      await navigator.clipboard.writeText(body);
      const shortBody =
        'Timeline list was copied to your clipboard — paste it here (Cmd/Ctrl+V).\n\n' +
        body.slice(0, 400) +
        (body.length > 400 ? '\n…' : '');
      window.open(
        `${gmailBase}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(shortBody)}`,
        '_blank',
        'noopener,noreferrer',
      );
      toast.success('Gmail opened — full list copied; paste into the body');
    } catch {
      window.open(
        `${gmailBase}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body.slice(0, 1200))}`,
        '_blank',
        'noopener,noreferrer',
      );
      toast.message('Gmail opened — list may be truncated; copy from Engine if needed');
    }
    return;
  }
  window.open(fullUrl, '_blank', 'noopener,noreferrer');
  toast.success('Gmail compose opened');
}

export type TasksTimelineBoardHandle = {
  goToToday: () => void;
  emailList: () => void;
};

const TasksTimelineBoard = forwardRef<TasksTimelineBoardHandle, TasksTimelineBoardProps>(
  function TasksTimelineBoard(
    {
      tasks,
      draggingId,
      onDragStart,
      onDragEnd,
      onOpenTask,
      onReschedule,
      updatingId,
      shareLabel = null,
      range,
    },
    ref,
  ) {
  const todayKey = useMemo(() => dateKey(new Date().toISOString()), []);
  const autoWindow = useMemo(() => computeMonthWindow(todayKey), [todayKey]);
  const scrollBehaviorRef = useRef<ScrollBehavior>('auto');

  const [manualStart, setManualStart] = useState<string | null>(null);
  const [extraRows, setExtraRows] = useState(0);
  const [dropDay, setDropDay] = useState<string | null>(null);
  const [floatPos, setFloatPos] = useState<{ x: number; y: number } | null>(null);
  const [viewportW, setViewportW] = useState(960);

  const suppressClickRef = useRef(false);
  const dropDayRef = useRef<string | null>(null);
  const taskDrag = useRef<{
    taskId: string;
    pointerId: number;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pendingCenterRef = useRef<string | null>(todayKey);

  useLayoutEffect(() => {
    scrollBehaviorRef.current = 'auto';
    pendingCenterRef.current = todayKey;
    setExtraRows(0);
    if (range === 'week') {
      setManualStart(alignToRowStart(todayKey, todayKey));
    } else {
      setManualStart(autoWindow.start);
    }
  }, [range, todayKey, autoWindow.start]);

  const weekStart = alignToRowStart(manualStart ?? todayKey, todayKey);
  const anchorStart = range === 'week' ? weekStart : (manualStart ?? autoWindow.start);
  const rowCount = range === 'week' ? 1 : autoWindow.rowCount + extraRows;

  const setDropDayBoth = useCallback((key: string | null) => {
    dropDayRef.current = key;
    setDropDay(key);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => setViewportW(el.clientWidth || 960);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rows = useMemo(
    () => buildWeekRows(anchorStart, rowCount),
    [anchorStart, rowCount],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, NetworkingTaskRow[]>();
    for (const t of tasks) {
      const k = dateKey(t.due_at);
      const list = map.get(k) ?? [];
      list.push(t);
      map.set(k, list);
    }
    for (const [, list] of map) {
      list.sort((a, b) => {
        const sa = a.strategy_score ?? -1;
        const sb = b.strategy_score ?? -1;
        if (sb !== sa) return sb - sa;
        return new Date(a.due_at).getTime() - new Date(b.due_at).getTime();
      });
    }
    return map;
  }, [tasks]);

  const draggingTask = useMemo(
    () => (draggingId ? tasks.find((t) => t.id === draggingId) ?? null : null),
    [tasks, draggingId],
  );

  const originDay = draggingTask ? dateKey(draggingTask.due_at) : null;
  const liveDay = dropDay ?? originDay;
  const movingTask = Boolean(draggingId && floatPos);

  const rowPitches = useMemo(
    () =>
      rows.map((row) => {
        let maxLoad = 0;
        for (const d of row.days) {
          let n = byDay.get(d)?.length ?? 0;
          // Live drop preview on an empty day still needs hang space
          if (movingTask && liveDay === d && n === 0) n = 1;
          if (n > maxLoad) maxLoad = n;
        }
        const forceHang =
          maxLoad > 0 ||
          row.days.includes(todayKey) ||
          (liveDay != null && row.days.includes(liveDay) && movingTask);
        return pitchForDayLoad(maxLoad, forceHang && maxLoad === 0);
      }),
    [rows, byDay, liveDay, todayKey, movingTask],
  );

  const rowTops = useMemo(() => {
    const tops: number[] = [];
    let y = 8;
    for (const p of rowPitches) {
      tops.push(y);
      y += p;
    }
    return tops;
  }, [rowPitches]);

  const boardHeight =
    (rowTops[rowTops.length - 1] ?? 0) +
    (rowPitches[rowPitches.length - 1] ?? ROW_PITCH_EMPTY) +
    8;

  const innerLeft = SIDE_PAD;
  const innerRight = Math.max(viewportW - SIDE_PAD, innerLeft + DAYS_PER_ROW * 64);
  const usableW = innerRight - innerLeft;
  const dayWidth = usableW / DAYS_PER_ROW;

  const centerOnDay = useCallback(
    (dayKey: string, behavior: ScrollBehavior = 'smooth') => {
      scrollBehaviorRef.current = behavior;
      pendingCenterRef.current = dayKey;
      setExtraRows(0);
      if (range === 'week') {
        setManualStart(alignToRowStart(dayKey, todayKey));
      } else {
        setManualStart(autoWindow.start);
      }
    },
    [todayKey, range, autoWindow.start],
  );

  const shiftWeek = useCallback(
    (dir: -1 | 1) => {
      const hardMin = addDays(todayKey, -MAX_PAST_DAYS);
      const hardMax = addDays(todayKey, MAX_AHEAD_DAYS);
      const next = addDays(weekStart, dir * DAYS_PER_ROW);
      if (next < hardMin || next > hardMax) return;
      setManualStart(next);
    },
    [todayKey, weekStart],
  );

  useLayoutEffect(() => {
    const dayKey = pendingCenterRef.current;
    if (!dayKey) return;
    const snappedStart =
      range === 'week' ? alignToRowStart(todayKey, todayKey) : autoWindow.start;
    if (extraRows !== 0) return;
    if (anchorStart !== snappedStart) return;
    let cancelled = false;
    let frames = 0;
    const jump = () => {
      if (cancelled) return;
      const scroller = scrollRef.current;
      const anchor =
        scroller?.querySelector<HTMLElement>(`[data-timeline-anchor="${dayKey}"]`) ??
        document.getElementById('eval-timeline-today');
      if (scroller && anchor) {
        pendingCenterRef.current = null;
        const nextTop =
          scroller.scrollTop +
          (anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top) -
          8;
        scroller.scrollTo({
          top: Math.max(0, nextTop),
          behavior: scrollBehaviorRef.current,
        });
        return;
      }
      if (frames++ < 12) requestAnimationFrame(jump);
    };
    requestAnimationFrame(jump);
    return () => {
      cancelled = true;
    };
  }, [range, todayKey, autoWindow.start, anchorStart, extraRows, boardHeight, rows]);

  useImperativeHandle(
    ref,
    () => ({
      goToToday: () => {
        centerOnDay(todayKey, 'smooth');
      },
      emailList: () => {
        const { subject, body } = buildTimelineShareEmail(tasks, {
          label: shareLabel,
          todayKey,
        });
        void openTimelineInGmail(subject, body);
      },
    }),
    [centerOnDay, todayKey, tasks, shareLabel],
  );

  // Grow future weeks while dragging near the bottom / scrolling down
  useEffect(() => {
    if (range === 'week' || !draggingId || !dropDay) return;
    const last = rows[rows.length - 1]?.days[DAYS_PER_ROW - 1];
    if (!last) return;
    if (dropDay >= addDays(last, -2) && extraRows < 4) {
      setExtraRows((n) => Math.min(4, n + 1));
    }
  }, [range, draggingId, dropDay, rows, extraRows]);

  const onSnakeScroll = () => {
    if (range === 'week') return;
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight > el.scrollHeight - 80 && extraRows < 8) {
      setExtraRows((n) => Math.min(8, n + 1));
    }
    if (el.scrollTop < 40) {
      // Prefetch past by shifting window up one week and keeping scroll position
      const hardMin = addDays(todayKey, -MAX_PAST_DAYS);
      const next = addDays(anchorStart, -DAYS_PER_ROW);
      if (next >= hardMin && next < anchorStart) {
        const pitch = ROW_PITCH_EMPTY;
        setManualStart(next);
        setExtraRows((n) => n + 1);
        requestAnimationFrame(() => {
          if (scrollRef.current) scrollRef.current.scrollTop += pitch;
        });
      }
    }
  };

  const finishTaskMove = useCallback(
    async (taskId: string, targetDay: string | null) => {
      const task = tasks.find((t) => t.id === taskId);
      setFloatPos(null);
      setDropDayBoth(null);
      onDragEnd();
      if (!task || !targetDay) return;
      if (dateKey(task.due_at) === targetDay) return;
      await onReschedule(task.id, applyDateKeepTime(task.due_at, targetDay));
    },
    [tasks, onDragEnd, onReschedule, setDropDayBoth],
  );

  const onTaskPointerDown = (e: React.PointerEvent, task: NetworkingTaskRow) => {
    if (updatingId === task.id) return;
    e.stopPropagation();
    e.preventDefault();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    taskDrag.current = {
      taskId: task.id,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    setDropDayBoth(dateKey(task.due_at));
    onDragStart(task.id);
  };

  const onTaskPointerMove = (e: React.PointerEvent) => {
    const s = taskDrag.current;
    if (!s || s.pointerId !== e.pointerId) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

    s.moved = true;
    suppressClickRef.current = true;
    setFloatPos({ x: e.clientX, y: e.clientY });
    const under = dayUnderPoint(e.clientX, e.clientY);
    if (under) setDropDayBoth(under);

    const box = scrollRef.current?.getBoundingClientRect();
    if (box && scrollRef.current) {
      const edge = 48;
      if (e.clientY > box.bottom - edge) scrollRef.current.scrollTop += 14;
      else if (e.clientY < box.top + edge) scrollRef.current.scrollTop -= 14;
    }
  };

  const onTaskPointerUp = (e: React.PointerEvent) => {
    const s = taskDrag.current;
    if (!s || s.pointerId !== e.pointerId) return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    const moved = s.moved;
    const taskId = s.taskId;
    taskDrag.current = null;
    if (!moved) {
      setFloatPos(null);
      setDropDayBoth(null);
      onDragEnd();
      return;
    }
    void finishTaskMove(taskId, dayUnderPoint(e.clientX, e.clientY) ?? dropDayRef.current);
  };

  const openTaskSafely = (task: NetworkingTaskRow) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onOpenTask(task);
  };

  return (
    <div className="w-full">
      {range === 'week' ? (
        <p className="mb-3 text-center text-sm font-medium text-gray-800 dark:text-gray-100 tabular-nums">
          {formatWeekSpan(weekStart)}
        </p>
      ) : null}
      <div ref={viewportRef} className="group/week relative w-full">
        {range === 'week' ? (
          <>
            <button
              type="button"
              onClick={() => shiftWeek(-1)}
              className="absolute left-1 z-20 -translate-y-1/2 p-1.5 rounded-full border border-gray-200 dark:border-gray-600 bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-200 shadow-sm opacity-0 pointer-events-none transition-opacity group-hover/week:opacity-100 group-hover/week:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
              style={{ top: LINE_CENTER_Y }}
              title="Previous week"
              aria-label="Previous week"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => shiftWeek(1)}
              className="absolute right-1 z-20 -translate-y-1/2 p-1.5 rounded-full border border-gray-200 dark:border-gray-600 bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-200 shadow-sm opacity-0 pointer-events-none transition-opacity group-hover/week:opacity-100 group-hover/week:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
              style={{ top: LINE_CENTER_Y }}
              title="Next week"
              aria-label="Next week"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        ) : null}
        <div
          ref={scrollRef}
          className={`relative w-full overflow-x-hidden overflow-y-auto ${
            movingTask ? 'cursor-grabbing' : 'cursor-default'
          }`}
          style={{ maxHeight: range === 'week' ? undefined : VIEWPORT_MAX_H }}
          onScroll={onSnakeScroll}
        >
          <div className="relative" style={{ width: '100%', height: boardHeight }}>
            {rows.map((row, r) => {
              const top = rowTops[r]!;
              const pitch = rowPitches[r]!;
              return (
                <div
                  key={`row-${r}-${row.days[0]}`}
                  className="absolute left-0 right-0"
                  style={{ top, height: pitch }}
                >
                  <div
                    className="absolute h-2 rounded-full bg-gray-400 dark:bg-slate-600 pointer-events-none"
                    style={{
                      left: innerLeft,
                      width: usableW,
                      top: LINE_Y_IN_ROW,
                      height: LINE_H,
                    }}
                    aria-hidden
                  />

                  {row.days.map((dayKey, col) => {
                    const x = innerLeft + (col + 0.5) * dayWidth;
                    const dayTasks = byDay.get(dayKey) ?? [];
                    const hasTasks = dayTasks.length > 0;
                    const isToday = dayKey === todayKey;
                    const isLive = liveDay === dayKey && movingTask;
                    const isOrigin = originDay === dayKey && movingTask;
                    const { title, sub } = formatDayShort(dayKey, todayKey);
                    const showHang = hasTasks || isLive;
                    const showLabel = hasTasks || isToday;
                    const hoverParts = formatRelativeDropParts(dayKey, todayKey);
                    const showHoverPanel = (isLive || !hasTasks) && !isToday;

                    return (
                      <div
                        key={dayKey}
                        data-day-drop={dayKey}
                        className={`group absolute flex flex-col items-center ${
                          isToday
                            ? 'z-[1] rounded-lg bg-emerald-200/40 dark:bg-emerald-400/15'
                            : ''
                        }`}
                        style={{
                          left: x,
                          top: 0,
                          width: Math.max(dayWidth - 6, 72),
                          height: pitch,
                          transform: 'translateX(-50%)',
                        }}
                      >
                        {isToday ? (
                          <span
                            id="eval-timeline-today"
                            data-timeline-anchor={dayKey}
                            className="absolute top-0 left-0 h-px w-px overflow-hidden"
                            aria-hidden
                          />
                        ) : (
                          <span data-timeline-anchor={dayKey} className="absolute top-0 left-0 h-px w-px overflow-hidden" aria-hidden />
                        )}
                        {/* Dot centered on the spine */}
                        {isToday ? (
                          <div
                            className="absolute left-1/2 w-0.5 h-5 -translate-x-1/2 -translate-y-1/2 bg-gray-900 dark:bg-white pointer-events-none"
                            style={{ top: LINE_CENTER_Y }}
                            aria-hidden
                          />
                        ) : null}
                        <div
                          className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 z-[1] ${
                            isToday
                              ? 'w-4 h-4 border-gray-900 bg-gray-900 dark:border-white dark:bg-white'
                              : isLive
                                ? 'w-3.5 h-3.5 border-gray-800 bg-gray-500 dark:border-slate-400 dark:bg-slate-400'
                                : hasTasks
                                  ? 'w-3.5 h-3.5 border-gray-700 bg-gray-600 dark:border-slate-400 dark:bg-slate-600'
                                  : 'w-3 h-3 border-gray-500 bg-white dark:border-slate-500 dark:bg-gray-900'
                          } ${isOrigin && !isLive ? 'opacity-40' : ''}`}
                          style={{ top: LINE_CENTER_Y }}
                          aria-hidden
                        />

                        {/* Labels + hang below the spine */}
                        <div
                          className="relative flex w-full flex-col items-center"
                          style={{ marginTop: LINE_CENTER_Y + 10 }}
                        >
                          {showLabel ? (
                            <span
                              className={`text-xs leading-none ${
                                isToday
                                  ? 'font-bold uppercase tracking-wide text-gray-900 dark:text-white'
                                  : 'tabular-nums font-semibold text-gray-800 dark:text-gray-200'
                              }`}
                            >
                              {isToday ? 'Today' : title.replace(/\s.*/, '')}
                            </span>
                          ) : null}

                          {showHoverPanel ? (
                            <div
                              className={`mt-1 z-[2] rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm dark:border-gray-600 dark:bg-gray-900 pointer-events-none ${
                                isLive
                                  ? 'opacity-100'
                                  : 'opacity-0 group-hover:opacity-100'
                              }`}
                            >
                              <p className="text-xs font-semibold leading-tight text-center text-gray-900 dark:text-white whitespace-nowrap">
                                {hoverParts.dateLine}
                              </p>
                              <p className="text-[11px] font-medium leading-tight text-center text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                {hoverParts.agoLine}
                              </p>
                            </div>
                          ) : null}

                          {showHang ? (
                            <div className="mt-2 flex flex-col items-center w-full px-0.5">
                              <div
                                className={`w-px h-2 ${
                                  isToday
                                    ? 'bg-gray-900 dark:bg-white'
                                    : 'bg-gray-400 dark:bg-slate-500'
                                }`}
                                aria-hidden
                              />
                              <div
                                className={`w-full rounded-lg border shadow-sm ${
                                  isToday
                                    ? 'border-gray-900 bg-white dark:border-white dark:bg-gray-900'
                                    : isLive
                                      ? 'border-dashed border-gray-600 bg-white dark:border-slate-400 dark:bg-gray-900'
                                      : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
                                }`}
                              >
                                <div className="px-2 pt-1.5 pb-1 border-b border-gray-200 dark:border-gray-700">
                                  {isLive ? (
                                    <>
                                      <p className="text-xs font-semibold truncate text-gray-900 dark:text-white">
                                        {hoverParts.dateLine}
                                      </p>
                                      <p className="text-[11px] text-gray-600 dark:text-gray-400 truncate">
                                        {hoverParts.agoLine}
                                      </p>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-xs font-semibold truncate text-gray-900 dark:text-white">
                                        {isToday ? 'Today' : title}
                                      </p>
                                      {!isToday ? (
                                        <p className="text-[11px] text-gray-600 dark:text-gray-400 truncate">
                                          {sub}
                                        </p>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                                <div className="p-1.5 space-y-1.5">
                                  {dayTasks.map((task) => {
                                    const overdue = isTaskOverdue(task.due_at, task.status);
                                    const high = isHighValueStrategyScore(task.strategy_score);
                                    const busyTask = updatingId === task.id;
                                    const isDraggingThis =
                                      draggingId === task.id && movingTask;
                                    return (
                                      <article
                                        key={task.id}
                                        data-task-chip
                                        role="button"
                                        tabIndex={0}
                                        onPointerDown={(e) => onTaskPointerDown(e, task)}
                                        onPointerMove={onTaskPointerMove}
                                        onPointerUp={onTaskPointerUp}
                                        onPointerCancel={onTaskPointerUp}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openTaskSafely(task);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            openTaskSafely(task);
                                          }
                                        }}
                                        title={
                                          STRATEGY_SCORE_LABELS[
                                            task.strategy_score as 1 | 2 | 3 | 4 | 5
                                          ] ?? 'Drag to another day'
                                        }
                                        className={`rounded-md border px-1.5 py-1 text-left cursor-grab active:cursor-grabbing touch-none focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                                          isDraggingThis ? 'opacity-30' : ''
                                        } ${busyTask ? 'opacity-50 pointer-events-none' : ''} ${
                                          task.status === 'done'
                                            ? 'border-gray-300 bg-[repeating-linear-gradient(135deg,#e5e7eb_0px,#e5e7eb_5px,#f3f4f6_5px,#f3f4f6_10px)] dark:border-gray-600 dark:bg-[repeating-linear-gradient(135deg,#374151_0px,#374151_5px,#1f2937_5px,#1f2937_10px)]'
                                            : overdue
                                              ? 'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30'
                                              : high
                                                ? 'border-violet-300 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/40'
                                                : 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800'
                                        }`}
                                      >
                                        <div className="flex items-start gap-1">
                                          <GripVertical
                                            className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-500"
                                            aria-hidden
                                          />
                                          <p
                                            className={`text-xs leading-snug line-clamp-2 ${
                                              task.status === 'done'
                                                ? 'text-gray-500 dark:text-gray-400 line-through'
                                                : 'text-gray-900 dark:text-white'
                                            }`}
                                          >
                                            {task.title || 'Untitled'}
                                          </p>
                                        </div>
                                      </article>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
},
);

export default TasksTimelineBoard;
